import { useState, type ReactNode } from 'react';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import {
  Barcode,
  Calendar,
  DollarSign,
  Hash,
  Mail,
  MapPin,
  Percent,
  Phone,
  Plus,
  Search,
  Table2,
  Type,
  X,
  ArrowRight,
} from 'lucide-react';
import type {
  LayoutExamplePage,
  LearnedLayoutSummary,
  LearningAccelerationReport,
} from '../shared/desktop-api';
import { LazyPageThumbnail } from './LazyPageThumbnail';
import { layoutTypeNames, layoutsOfType } from '../shared/layout-type-names';
// PAIGE: the type's own pages from Paige's engine host, before CapturePoint's one (paige/type-own-pages.ts).
import { typeOwnPages } from '../paige/type-own-pages';
import { typeShades } from './type-tints';
import { SKILL_STATE_LABEL, type TypeLearningState } from '../shared/learning-skills';
import { SkillGlyph } from './LearningJourneyMap';
import './learning-journey-map.css';
import './setup-type-deck.css';

type DeckField = { id: string; name: string; kind: string };
type DeckType = { id: string; name: string; fields: readonly DeckField[] };

/** Below this share read without review, a card's bar turns amber. A cue, not a gate. */
const REVIEW_CUE = 90;

/**
 * A type's documents, how many went through with nobody looking, and how many examples
 * someone confirmed (taught), summed over the names its reports use.
 */
export function typeReading(type: DeckType, report: LearningAccelerationReport | null | undefined) {
  const names = layoutTypeNames(type, report);
  let documents = 0,
    withoutReview = 0,
    confirmed = 0,
    measured = false;
  for (const entry of report?.documentTypes ?? []) {
    if (!names.has(entry.documentType.trim().toLocaleLowerCase())) continue;
    documents += entry.documents ?? entry.confirmed + entry.waiting;
    confirmed += entry.confirmed;
    if (entry.readWithoutReview != null && entry.documents != null) {
      withoutReview += entry.readWithoutReview;
      measured = true;
    }
  }
  return { documents, withoutReview: measured ? withoutReview : null, confirmed };
}

/** A real page of the type's own documents, for a card whose learned layouts have no picture yet; confirmed first. */
function typeExamplePage(
  type: DeckType,
  report: LearningAccelerationReport | null | undefined,
): LayoutExamplePage | null {
  const names = layoutTypeNames(type, report);
  const pages = (report?.documentTypes ?? [])
    .filter(
      (entry) => names.has(entry.documentType.trim().toLocaleLowerCase()) && entry.examplePage,
    )
    .map((entry) => entry.examplePage!);
  return pages.find((page) => page.confirmed) ?? pages[0] ?? null;
}

export function kindIcon(kind: string, size = 14) {
  const Icon =
    kind === 'line-items'
      ? Table2
      : kind === 'identifier' || kind === 'number' || kind === 'integer' || kind === 'decimal'
        ? Hash
        : kind === 'date' || kind === 'date-time'
          ? Calendar
          : kind === 'currency'
            ? DollarSign
            : kind === 'percentage'
              ? Percent
              : kind === 'phone'
                ? Phone
                : kind === 'email'
                  ? Mail
                  : kind === 'postal-address'
                    ? MapPin
                    : kind === 'barcode'
                      ? Barcode
                      : Type;
  return <Icon size={size} aria-hidden="true" />;
}

/**
 * Document types as a deck of cards: each type shows real pages of its documents
 * and opens its editor. Every number on a card is a
 * count the engine measured; nothing is estimated here.
 */
export function SetupTypeDeck({
  types,
  report,
  learning,
  loading,
  query,
  onQuery,
  onOpen,
  onAdd,
  actions,
}: {
  types: readonly DeckType[];
  report: LearningAccelerationReport | null;
  /** Each type's place in Learning (by type id), as the Learning screen works it out; absent until read. */
  learning?: ReadonlyMap<string, TypeLearningState | null>;
  loading: boolean;
  query: string;
  onQuery: (query: string) => void;
  onOpen: (typeId: string) => void;
  /** Absent when this job's structure is owned elsewhere. */
  onAdd?: () => void;
  actions?: ReactNode;
}) {
  const [order, setOrder] = useState<'used' | 'name'>('used');
  const search = query.trim().toLocaleLowerCase();
  const matches = (text: string) => text.toLocaleLowerCase().includes(search);
  const shades = typeShades(types);
  const byName = (left: DeckType, right: DeckType) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base', numeric: true });
  const used = (type: DeckType) => typeReading(type, report).documents;
  const shown = types
    .filter(
      (type) => !search || matches(type.name) || type.fields.some((field) => matches(field.name)),
    )
    // Most used keeps the job's own order among equals, so a job with nothing read yet reads as set up.
    .sort(order === 'name' ? byName : (left, right) => used(right) - used(left));
  return (
    <section className="setup-deck" aria-labelledby="setup-deck-title">
      <header className="setup-deck-head">
        <h2 id="setup-deck-title">
          {types.length === 0
            ? 'Document types'
            : types.length === 1
              ? '1 document type'
              : `${types.length} document types`}
        </h2>
        <div className="setup-deck-tools">
          {types.length > 1 && (
            <div className="setup-deck-order" role="group" aria-label="Order types">
              <button
                type="button"
                aria-pressed={order === 'used'}
                onClick={() => setOrder('used')}
              >
                Most used
              </button>
              <button
                type="button"
                aria-pressed={order === 'name'}
                onClick={() => setOrder('name')}
              >
                A–Z
              </button>
            </div>
          )}
          {types.length > 1 && (
            <label className="setup-deck-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                aria-label="Find a type or field"
                placeholder="Find a type or field"
                value={query}
                onChange={(event) => onQuery(event.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Clear search"
                  onClick={() => onQuery('')}
                >
                  <X size={14} />
                </button>
              )}
            </label>
          )}
          {actions}
          {onAdd && types.length > 0 && (
            <button type="button" className="primary-button setup-deck-new" onClick={onAdd}>
              <Plus size={16} />
              New type
            </button>
          )}
        </div>
      </header>
      {/* No types yet: one large place to start, drawn as the deck it will become, with its one action. */}
      {types.length === 0 ? (
        <div className="setup-deck-empty">
          <div className="setup-deck-empty-stage" aria-hidden="true">
            <DeckFan layouts={[]} loading={false} outlines={3} />
          </div>
          <h3>What would you like to capture?</h3>
          <p>Each kind of document gets its own fields and tables.</p>
          {onAdd && (
            <button type="button" className="primary-button" onClick={onAdd}>
              <Plus size={17} />
              Add your first document type
            </button>
          )}
        </div>
      ) : (
        <ul className="setup-deck-grid" aria-label="Document types">
          {shown.map((type) => (
            <DeckCard
              key={type.id}
              type={type}
              shade={shades.get(type.name) ?? 0}
              report={report}
              learning={learning?.get(type.id) ?? null}
              loading={loading}
              onOpen={() => onOpen(type.id)}
              matched={
                search && !matches(type.name)
                  ? type.fields.filter((field) => matches(field.name)).map((field) => field.name)
                  : []
              }
            />
          ))}
          {search && shown.length === 0 && (
            <li className="setup-deck-none" role="status">
              No matching types or fields.
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function DeckCard({
  type,
  shade,
  report,
  learning,
  loading,
  onOpen,
  matched,
}: {
  type: DeckType;
  shade: number;
  report: LearningAccelerationReport | null;
  learning: TypeLearningState | null;
  loading: boolean;
  onOpen: () => void;
  /** Field names a search matched when the type name did not; the card says why it is shown. */
  matched: readonly string[];
}) {
  const layouts = layoutsOfType(type, report).sort(
    (left, right) => right.documents - left.documents,
  );
  const { documents, withoutReview } = typeReading(type, report);
  // One document read without review out of hundreds is 1%, never 0.
  const share =
    documents > 0 && withoutReview ? Math.max(1, Math.round((withoutReview / documents) * 100)) : 0;
  const low = share > 0 && share < REVIEW_CUE;
  const skill = learning === 'ready' ? 'learned' : learning;
  const fields = type.fields.filter(
    (field) => field.kind !== 'document-type' && field.kind !== 'line-items',
  );
  const tables = type.fields.filter((field) => field.kind === 'line-items');
  const summary = [
    `${fields.length} ${fields.length === 1 ? 'field' : 'fields'}`,
    ...tables.map((table) => table.name),
  ].join(' · ');
  return (
    <li className="setup-deck-slot">
      <article className="setup-deck-card">
        {/* The whole card is the one button that opens the type; "Open" only shows where to click. */}
        <button
          type="button"
          className="setup-deck-open"
          aria-label={`Open ${type.name}`}
          onClick={onOpen}
        />
        <div className="setup-deck-stage" data-type-shade={shade} aria-hidden="true">
          <DeckFan
            layouts={layouts}
            ownPages={typeOwnPages(type, report)}
            fallback={typeExamplePage(type, report)}
            loading={loading}
          />
        </div>
        <div className="setup-deck-body">
          <h3>{type.name}</h3>
          <p>
            {matched.length ? (
              <span className="setup-deck-match">Matches: {matched.join(', ')}</span>
            ) : (
              summary
            )}
          </p>
          {/* The card says what Learning says (owner, 2026-10-19): Not taught, Learning or Ready for the part of the
          type furthest behind. Once documents go through on their own, the share read without review takes that
          place, where Learning says In use. A type with no documents yet says nothing more. */}
          {share > 0 ? (
            <div className="setup-deck-meter">
              <div className="setup-deck-mrow">
                <span>Read without review</span>
                <strong className={low ? 'is-low' : undefined}>{share}%</strong>
              </div>
              <div className="setup-deck-track" aria-hidden="true">
                <div
                  className={`setup-deck-fill${low ? ' is-low' : ''}`}
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
          ) : (
            documents > 0 &&
            skill && (
              <div className="setup-deck-meter setup-deck-learning">
                <div className="setup-deck-mrow">
                  <span>
                    <SkillGlyph state={skill} />
                    {SKILL_STATE_LABEL[skill]}
                  </span>
                </div>
              </div>
            )
          )}
          <div className="setup-deck-foot">
            <span className="setup-deck-count">
              {documents > 0
                ? `${documents.toLocaleString()} ${documents === 1 ? 'document' : 'documents'}`
                : loading
                  ? ''
                  : 'No documents yet'}
            </span>
            <span className="setup-deck-go" aria-hidden="true">
              Open
              <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </article>
    </li>
  );
}

/**
 * Up to three example pages of the type, fanned: a real page where a learned layout has one,
 * otherwise one real page of the type's own documents, otherwise an outline.
 */
function DeckFan({
  layouts,
  ownPages = [],
  fallback = null,
  loading,
  outlines = 1,
}: {
  layouts: readonly LearnedLayoutSummary[];
  /** PAIGE: up to three of the type's own pages (Paige's engine host), fanned before `fallback`. */
  ownPages?: readonly LayoutExamplePage[];
  fallback?: LayoutExamplePage | null;
  loading: boolean;
  outlines?: 1 | 2 | 3;
}) {
  const shown = layouts.slice(0, 3);
  const pages: (LayoutExamplePage | null)[] = shown.some((layout) => layout.examplePage)
    ? shown.map((layout) => layout.examplePage ?? null)
    : ownPages.length // PAIGE
      ? [...ownPages]
      : fallback
        ? [fallback]
        : shown.length
          ? shown.map(() => null)
          : Array.from({ length: outlines }, () => null);
  const angles = pages.length === 3 ? [-10, 0, 10] : pages.length === 2 ? [-7, 7] : [0];
  // In percent of a page's own width, so the fan widens with the card and every page stays inside it.
  const offsets = pages.length === 3 ? [-6, 0, 6] : pages.length === 2 ? [-8, 8] : [0];
  return (
    <div className={`setup-deck-fan${loading && !shown.length ? ' is-loading' : ''}`}>
      {pages.map((page, index) => (
        <span
          key={page ? `${page.documentId}-${index}` : `outline-${index}`}
          className="setup-deck-page"
          style={{
            transform: `translateX(${offsets[index]}%) translateY(${Math.abs(angles[index])}px) rotate(${angles[index]}deg)`,
            zIndex: index === 1 || pages.length === 1 ? 3 : 2 - (index === 0 ? 1 : 0),
          }}
        >
          {page ? (
            <LazyPageThumbnail previewPath={page.previewPath} label="" />
          ) : (
            <span className="setup-deck-outline">
              <i />
              <i />
              <i />
              <i />
              <i />
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
